import React, { createContext, useCallback, useContext, useEffect, useState } from "react";

import API_BASE_URL from "../config/api";
import { setSessionTokenGetter } from "./sessionToken";
import { supabase } from "./supabase";
import { applyThemePreference } from "../utils/userPreferences";

const AuthContext = createContext(null);
const SESSION_INIT_TIMEOUT_MS = 5000;
const PROFILE_TIMEOUT_MS = 5000;

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [sessionLoaded, setSessionLoaded] = useState(false);
  const [user, setUser] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const sessionUserId = session?.user?.id || null;
  const isGuest = Boolean(session?.user?.is_anonymous);
  // Keep profile loading independent from access-token rotations. Supabase refreshes
  // tokens when a background tab becomes active, but the user identity is unchanged.
  const sessionEmail = session?.user?.email || null;
  const sessionPhone = session?.user?.phone || null;
  const sessionMetadata = session?.user?.user_metadata || {};
  const sessionFirstName = sessionMetadata.first_name || sessionMetadata.given_name || "";
  const sessionLastName = sessionMetadata.last_name || sessionMetadata.family_name || "";

  const refreshUser = useCallback(async () => {
    if (!sessionUserId || isGuest) {
      setUser(null);
      setProfileLoaded(true);
      return null;
    }

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), PROFILE_TIMEOUT_MS);
    let response;
    try {
      response = await fetch(`${API_BASE_URL}/api/auth/me`, { signal: controller.signal });
    } catch {
      response = null;
    } finally {
      clearTimeout(timeout);
    }

    if (!response?.ok) {
      if (response?.status === 401 || response?.status === 403) {
        await supabase.auth.signOut({ scope: "local" });
        setUser(null);
        setProfileLoaded(true);
        return null;
      }
      const fallbackUser = {
        id: sessionUserId,
        email: sessionEmail,
        phone: sessionPhone,
        first_name: sessionFirstName,
        last_name: sessionLastName,
        roles: [],
        avatar: null,
        profile_unavailable: true,
      };
      setUser(fallbackUser);
      setProfileLoaded(true);
      return fallbackUser;
    }

    const profile = await response.json();
    const guestAccessToken = sessionStorage.getItem("akkorent_guest_access_token");
    if (guestAccessToken) {
      const mergeResponse = await fetch(`${API_BASE_URL}/api/guest/merge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ guest_access_token: guestAccessToken }),
      });
      if (mergeResponse.ok) {
        const merged = await mergeResponse.json();
        if (merged.recently_viewed?.length) {
          localStorage.setItem("akkorent_recently_viewed", JSON.stringify(merged.recently_viewed));
        }
        if (!merged.retry_required) sessionStorage.removeItem("akkorent_guest_access_token");
      }
    }
    // A navbar theme choice is stored locally immediately. Do not let a stale
    // profile preference overwrite it during the next auth refresh.
    const hasManualTheme = localStorage.getItem("theme_source") === "manual";
    if (profile.preferences?.theme && !hasManualTheme) {
      applyThemePreference(profile.preferences.theme);
    }
    setUser(profile);
    setProfileLoaded(true);
    return profile;
  }, [sessionUserId, isGuest, sessionEmail, sessionPhone, sessionFirstName, sessionLastName]);

  useEffect(() => {
    let active = true;
    let authEventReceived = false;
    setSessionTokenGetter(async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token || null;
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      authEventReceived = true;
      setSession(nextSession);
      setSessionLoaded(true);
    });
    const initTimeout = setTimeout(() => {
      if (active && !authEventReceived) setSessionLoaded(true);
    }, SESSION_INIT_TIMEOUT_MS);
    supabase.auth.getSession().then(({ data }) => {
      if (!active || authEventReceived) return;
      setSession(data.session);
      setSessionLoaded(true);
    }).catch(() => {
      if (active && !authEventReceived) setSessionLoaded(true);
    });
    return () => {
      active = false;
      clearTimeout(initTimeout);
      listener.subscription.unsubscribe();
      setSessionTokenGetter(null);
    };
  }, []);

  useEffect(() => {
    if (isGuest && session?.access_token) {
      sessionStorage.setItem("akkorent_guest_access_token", session.access_token);
    }
  }, [isGuest, session?.access_token]);

  useEffect(() => {
    if (!sessionLoaded) return;
    setProfileLoaded(false);
    refreshUser().catch(() => setProfileLoaded(true));
  }, [refreshUser, sessionLoaded]);

  const logout = useCallback(async (scope = "local") => {
    const { error } = await supabase.auth.signOut({ scope });
    if (error) throw error;
    if (scope !== "others") setUser(null);
  }, []);

  const acceptSession = useCallback((nextSession) => {
    setSession(nextSession);
    setSessionLoaded(true);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        isAuthenticated: Boolean(session && !isGuest),
        isGuest,
        hasSession: Boolean(session),
        logout,
        authReady: Boolean(sessionLoaded && (!session || profileLoaded)),
        session,
        acceptSession,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
